import React from 'react';
import { Dialog, Box, Typography, IconButton, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CustomButton from './customButton';
import tickIcon from '../Assets/images/benefit-tick.png';

export default function PaymentStatusModal({
  open,
  onClose,
  status = 'success',
  title,
  subtitle,
  items = [],
  backText = 'Back',
  onBack,
  tryAgainText = 'Try Again',
  onTryAgain,
}) {
  const isSuccess = status === 'success';
  const isLoading = status === 'loading';

  const resolvedTitle =
    title || (isSuccess ? 'Payment successful' : isLoading ? 'Processing...' : 'Payment unsuccessful');
  const resolvedSubtitle =
    subtitle ||
    (isSuccess
      ? 'Your subscription is now active.'
      : isLoading
      ? 'Please wait while we process your request...'
      : "We couldn't process your payment.");

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ position: 'relative', p: 3 }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, opacity: isLoading ? 0.3 : 0.5 }}
          disabled={isLoading}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            p: { xs: 1, sm: 2 },
          }}
        >
          <Typography
            sx={{
              color: isSuccess ? '#3D9A50' : isLoading ? '#600DB4' : '#D93D42',
              fontWeight: 600,
              fontSize: 22,
              mb: 1,
            }}
          >
            {resolvedTitle}
          </Typography>
          <Typography sx={{ color: '#6B7280', fontSize: 14, mb: 3 }}>
            {resolvedSubtitle}
          </Typography>

          {isLoading ? (
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                background: '#F3F4F6',
                border: '2px solid #E5E7EB',
              }}
            >
              <CircularProgress 
                size={60} 
                thickness={4}
                sx={{ color: '#600DB4' }}
              />
            </Box>
          ) : isSuccess ? (
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                background: '#EBF9EB',
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 80, color: '#3D9A50' }} />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 3,
              }}
            >
              {/* Main failure icon with layered borders */}
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#D93D42',
                  border: '2px solid #F5F5F5',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '-6px',
                    left: '-6px',
                    right: '-6px',
                    bottom: '-6px',
                    border: '2px dashed #E3F2FD',
                    borderRadius: '50%',
                  },
                }}
              >
                <CancelIcon sx={{ fontSize: 60, color: '#FFFFFF' }} />
              </Box>
            </Box>
          )}

          {/* Instructions for failure case */}
          {!isSuccess && !isLoading && (
            <Typography
              sx={{
                color: '#6B7280',
                fontSize: 14,
                mb: 3,
                maxWidth: '400px',
                lineHeight: 1.5,
              }}
            >
              Please check your card details or try another payment method to
              continue adding listings.
            </Typography>
          )}

          {Array.isArray(items) && items.length > 0 && isSuccess && (
            <Box
              sx={{
                width: '100%',
                border: '1px solid rgba(229,231,235,1)',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Box
                sx={{
                  background: '#F9FAFB',
                  borderRadius: 1,
                  p: 2,
                  textAlign: 'left',
                }}
              >
                <Typography
                  sx={{
                    color: '#1B2538',
                    fontWeight: 600,
                    fontSize: 16,
                    mb: 1,
                  }}
                >
                  You can now
                </Typography>
                {items.map((text, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <img
                      src={tickIcon}
                      alt="tick"
                      style={{ width: '25px', height: '25px' }}
                    />
                    <Typography sx={{ color: '#7C8694', fontSize: 14 }}>
                      {text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
              mt: 3,
              gap: 2,
            }}
          >
            {!isLoading && (
              <CustomButton
                onClick={onBack || onClose}
                title={backText}
                type="tertiary"
              />
            )}
            {!isSuccess && !isLoading && onTryAgain && (
              <CustomButton
                onClick={onTryAgain}
                title={tryAgainText}
                type="primary"
              />
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
